import React from 'react';
import { XMarkIcon, UserIcon, EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import FormInput from './FormInput';
import FormTextarea from './FormTextarea';
import PhoneInput from '../common/PhoneInput';
import useFormValidation from '../../hooks/useFormValidation';
import { validationSchemas, sanitizers } from '../../utils/validation';

interface Customer {
  id?: number;
  first_name: string;
  last_name: string;
  email?: string; // Now optional
  phone: string;
  country_code?: string;
  phone_number?: string;
  address: string;
}

interface CustomerFormProps {
  customer?: Customer | null;
  onSubmit: (data: Partial<Customer>) => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const initialData = customer || {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_code: '965', // Default to Kuwait
    phone_number: '',
    address: '',
  };

  const {
    formData,
    errors,
    touched,
    isValid,
    isSubmitting,
    handleSubmit,
    getFieldProps: getFieldPropsOriginal,
  } = useFormValidation({
    validationSchema: validationSchemas.customer,
    initialData,
    sanitizers: {
      first_name: sanitizers.trim,
      last_name: sanitizers.trim,
      email: (value: string) => value ? sanitizers.trim(value).toLowerCase() : '',
      phone: sanitizers.phoneNumber,
      country_code: sanitizers.trim,
      phone_number: sanitizers.phoneNumber,
      address: sanitizers.trim,
    },
    onSubmit: async (data) => {
      await onSubmit(data);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Wrapper to ensure proper typing for FormInput
  const getFieldProps = (fieldName: string) => {
    const props = getFieldPropsOriginal(fieldName);
    return props;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <UserIcon className="w-6 h-6 mr-2 text-[#008B8B]" />
              {customer ? 'Edit Customer' : 'Add New Customer'}
            </h2>
            <button
              onClick={onCancel}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name Field */}
            <FormInput
              label="First Name"
              placeholder="Enter customer's first name"
              required
              leftIcon={<UserIcon className="w-5 h-5" />}
              loading={isSubmitting}
              {...getFieldProps('first_name')}
            />

            {/* Last Name Field */}
            <FormInput
              label="Last Name"
              placeholder="Enter customer's last name"
              required
              leftIcon={<UserIcon className="w-5 h-5" />}
              loading={isSubmitting}
              {...getFieldProps('last_name')}
            />

            {/* Email Field (Optional) */}
            <FormInput
              label="Email Address"
              type="email"
              placeholder="customer@example.com (optional)"
              required={false}
              leftIcon={<EnvelopeIcon className="w-5 h-5" />}
              loading={isSubmitting}
              {...getFieldProps('email')}
            />

            {/* Phone Field */}
            <PhoneInput
              label="Phone Number"
              countryCode={formData.country_code || '965'}
              phoneNumber={formData.phone_number || ''}
              onCountryCodeChange={(code) => {
                const syntheticEvent = {
                  target: { name: 'country_code', value: code },
                  currentTarget: { name: 'country_code', value: code },
                  type: 'change',
                  preventDefault: () => {},
                  stopPropagation: () => {},
                } as any;
                getFieldPropsOriginal('country_code').onChange(syntheticEvent);
              }}
              onPhoneNumberChange={(number) => {
                const syntheticEvent = {
                  target: { name: 'phone_number', value: number },
                  currentTarget: { name: 'phone_number', value: number },
                  type: 'change',
                  preventDefault: () => {},
                  stopPropagation: () => {},
                } as any;
                getFieldPropsOriginal('phone_number').onChange(syntheticEvent);
                // Also update the legacy phone field for backward compatibility
                const phoneEvent = {
                  target: { name: 'phone', value: `+${formData.country_code}${number}` },
                  currentTarget: { name: 'phone', value: `+${formData.country_code}${number}` },
                  type: 'change',
                  preventDefault: () => {},
                  stopPropagation: () => {},
                } as any;
                getFieldPropsOriginal('phone').onChange(phoneEvent);
              }}
              required
              disabled={isSubmitting}
              error={errors.phone_number || errors.phone}
            />

            {/* Address Field */}
            <FormTextarea
              label="Address"
              placeholder="Enter full address including area, block, and street"
              rows={3}
              maxLength={500}
              showCharCount
              loading={isSubmitting}
              {...getFieldProps('address')}
            />

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isValid || isSubmitting || loading}
                className="px-4 py-2 bg-[#008B8B] text-white rounded-md hover:bg-[#006666] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {(isSubmitting || loading) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                )}
                {customer ? 'Update Customer' : 'Add Customer'}
              </button>
            </div>
          </form>

          {/* Form Summary (Development Helper) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Form State</h3>
              <div className="text-xs space-y-1">
                <div>Valid: <span className={isValid ? 'text-green-600' : 'text-red-600'}>{isValid ? 'Yes' : 'No'}</span></div>
                <div>Errors: {Object.keys(errors).length}</div>
                <div>Touched: {Object.keys(touched).filter(key => touched[key]).length}</div>
                {Object.keys(errors).length > 0 && (
                  <div className="mt-2">
                    <div className="text-red-600">Errors:</div>
                    {Object.entries(errors).map(([field, error]) => (
                      <div key={field} className="ml-2">• {field}: {typeof error === 'string' ? error : (error as any)?.message || 'Invalid value'}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerForm;